import { parseCommentNote, readCommentMetadata } from "@/utils/comment-note"
import type { SessionMessageInfo } from "@opencode-ai/client/promise"
import { AssistantMessage, Part, SessionStatus, UserMessage } from "@opencode-ai/sdk/v2"
import { groupParts, renderable, type PartGroup } from "@opencode-ai/session-ui/message-part"
import { TimelineRow, type SummaryDiff } from "./timeline-row"
import { uniqueSummaryDiffs } from "./summary-diffs"
import { compareMessages } from "@/utils/session-message"
import {
  isTranslationTextTool,
  toolHasProduct,
  translationText,
  translationDownloads,
  translationDownloadReferenced,
} from "@opencode-ai/session-ui/message-file"

export { TimelineRow, type SummaryDiff } from "./timeline-row"

export type TimelineRowMap = {
  TurnGap: { userMessageID: string }
  CommentStrip: {
    userMessageID: string
  }
  UserMessage: {
    userMessageID: string
    anchor: boolean
  }
  TurnDivider: {
    userMessageID: string
    label: "compaction" | "interrupted"
  }
  AssistantPart: {
    userMessageID: string
    group: PartGroup
    previousAssistantPart: boolean
  }
  Thinking: { userMessageID: string; reasoningHeading?: string }
  Retry: { userMessageID: string }
  DiffSummary: { userMessageID: string; diffs: SummaryDiff[] }
  Error: { userMessageID: string; text: string }
}

export namespace Timeline {
  export function constructSessionMessageRows(
    messages: SessionMessageInfo[],
    getMessage: (messageID: string) => UserMessage | AssistantMessage | undefined,
    getMessageParts: (messageID: string) => Part[],
    showReasoning: boolean,
    status: SessionStatus["type"],
    inlineComments: boolean,
    projectedUserMessages: UserMessage[],
    turnCollapsedOverride: Record<string, boolean | undefined> = {},
  ) {
    const turns: { user: UserMessage; assistants: AssistantMessage[] }[] = []
    const turnByUserID = new Map<string, (typeof turns)[number]>()
    messages.forEach((message) => {
      const projected = getMessage(message.id)
      if (message.type === "shell" && projected?.role === "user") {
        const assistant = getMessage(`${message.id}:assistant`)
        const turn = { user: projected, assistants: assistant?.role === "assistant" ? [assistant] : [] }
        turns.push(turn)
        turnByUserID.set(projected.id, turn)
        return
      }
      if (projected?.role === "user") {
        if (turnByUserID.has(projected.id)) return
        const turn = { user: projected, assistants: [] }
        turns.push(turn)
        turnByUserID.set(projected.id, turn)
        return
      }
      if (projected?.role !== "assistant") return
      const existing = turnByUserID.get(projected.parentID)
      if (existing) {
        existing.assistants.push(projected)
        return
      }
      const user = getMessage(projected.parentID)
      if (user?.role !== "user") return
      const turn = { user, assistants: [projected] }
      turns.push(turn)
      turnByUserID.set(user.id, turn)
    })
    projectedUserMessages.forEach((user) => {
      if (turnByUserID.has(user.id)) return
      const turn = { user, assistants: [] }
      const index = turns.findIndex((item) => compareMessages(user, item.user) < 0)
      if (index < 0) turns.push(turn)
      if (index >= 0) turns.splice(index, 0, turn)
      turnByUserID.set(user.id, turn)
    })

    // Some turns are auto-injected tool回环 messages (role=user but the text is a tool result,
    // e.g. "## SonarQube Status: [WARN]"). The backend sometimes parents the real request's
    // assistant work to one of these echo messages, which would otherwise render the work under a
    // hidden bubble and push the real user bubble out of order. Fold each echo turn's assistants
    // into the nearest real turn (previous if present, else next) and drop the echo turns.
    const isEcho = (turn: (typeof turns)[number]) => isToolEchoMessage(turn.user, getMessageParts)
    turns.forEach((turn, i) => {
      if (!isEcho(turn) || turn.assistants.length === 0) return
      let target: (typeof turns)[number] | undefined
      for (let j = i - 1; j >= 0; j--) {
        if (!isEcho(turns[j])) {
          target = turns[j]
          break
        }
      }
      if (!target) {
        for (let j = i + 1; j < turns.length; j++) {
          if (!isEcho(turns[j])) {
            target = turns[j]
            break
          }
        }
      }
      if (target) {
        target.assistants.push(...turn.assistants)
        turn.assistants = []
      }
    })
    const visibleTurns = turns.filter((turn) => !isEcho(turn))

    const activeMessageID = visibleTurns.at(-1)?.user.id
    return {
      activeMessageID,
      rows: visibleTurns.flatMap((turn, index) =>
        constructMessageRows(
          turn.user,
          getMessageParts,
          turn.assistants,
          index,
          showReasoning,
          status,
          turn.user.id === activeMessageID,
          inlineComments,
          turnCollapsedOverride[turn.user.id],
        ),
      ),
    }
  }

  const SONARQUBE_STATUS_PATTERN = /^##\s+SonarQube\s+(Status:|Analysis\s+Results)/

  export function isToolEchoMessage(userMessage: UserMessage, getMessageParts: (messageID: string) => Part[]): boolean {
    const firstTextPart = getMessageParts(userMessage.id).find((p) => p.type === "text")
    return firstTextPart?.type === "text" && SONARQUBE_STATUS_PATTERN.test(firstTextPart.text.trimStart())
  }

  export function constructMessageRows(
    userMessage: UserMessage,
    getMessageParts: (messageID: string) => Part[],
    assistantMessages: AssistantMessage[],
    index: number,
    showReasoning: boolean,
    status: SessionStatus["type"],
    isActive: boolean,
    // v2 renders comments inside the user message attachments row instead of a strip row
    inlineComments: boolean,
    turnCollapsedOverride?: boolean,
  ) {
    const rows: TimelineRow.TimelineRow[] = []

    const previousUserMessage = index > 0
    const userParts = getMessageParts(userMessage.id)

    const comments = userParts.flatMap((p) => MessageComment.fromPart(p) ?? [])
    const compaction = userParts.some((p) => p.type === "compaction")
    const interruptedMessageIndex = assistantMessages.findIndex((m) => m.error?.name === "MessageAbortedError")
    const interrupted = interruptedMessageIndex !== -1
    const latestError = assistantMessages.at(-1)?.error
    const error = latestError?.name === "MessageAbortedError" ? undefined : latestError

    const assistantPartRefs = assistantMessages.flatMap((message, messageIndex) =>
      getMessageParts(message.id)
        .filter((part) => renderable(part, showReasoning))
        .map((part) => ({ messageID: message.id, messageIndex, part })),
    )
    const assistantItems =
      interrupted && !compaction
        ? [
            ...groupParts(assistantPartRefs.filter((ref) => ref.messageIndex <= interruptedMessageIndex)).map(
              (group) => ({
                type: "part" as const,
                group,
              }),
            ),
            { type: "interrupted" as const },
            ...groupParts(assistantPartRefs.filter((ref) => ref.messageIndex > interruptedMessageIndex)).map(
              (group) => ({
                type: "part" as const,
                group,
              }),
            ),
          ]
        : groupParts(assistantPartRefs).map((group) => ({ type: "part" as const, group }))
    if (previousUserMessage) rows.push(new TimelineRow.TurnGap({ userMessageID: userMessage.id }))

    if (comments.length > 0 && !inlineComments)
      rows.push(
        new TimelineRow.CommentStrip({
          userMessageID: userMessage.id,
        }),
      )

    rows.push(
      new TimelineRow.UserMessage({
        userMessageID: userMessage.id,
        anchor: inlineComments || comments.length === 0,
      }),
    )

    if (compaction) {
      rows.push(
        new TimelineRow.TurnDivider({
          userMessageID: userMessage.id,
          label: "compaction",
        }),
      )
    }

    let assistantGroupIndex = 0
    const working = isActive && status === "busy" && !error
    // Only translation-only turns suppress duplicate model narration. A mixed
    // turn must keep other experts' text, reasoning and tool expansion behavior.
    const translationTurn =
      assistantPartRefs.some(
        ({ part }) =>
          isTranslationTextTool(part) ||
          (part.type === "tool" &&
            (part.tool === "translation_attachment_upload" ||
              /^translate-mcp_(upload_document|configure_document_translation|create_document_translation|get_document_translation)/.test(
                part.tool,
              ))),
      ) &&
      assistantPartRefs.every(
        ({ part }) =>
          part.type !== "tool" ||
          part.tool.startsWith("translate-mcp_") ||
          ["translation_attachment_list", "translation_attachment_upload"].includes(part.tool) ||
          (part.tool === "skill" &&
            typeof part.state.input.name === "string" &&
            ["fy-text", "fy-doc", "translation-service"].includes(part.state.input.name)),
      )
    const hasTranslation = translationTurn && assistantPartRefs.some((ref) => translationText(ref.part) !== undefined)
    const documentTurn =
      translationTurn &&
      assistantPartRefs.some(
        ({ part }) =>
          part.type === "tool" &&
          (part.tool === "translation_attachment_upload" ||
            /^translate-mcp_(upload_document|configure_document_translation|create_document_translation|get_document_translation)/.test(
              part.tool,
            )),
      )
    const lastToolIndex = assistantPartRefs.findLastIndex(({ part }) => part.type === "tool")
    const finalDocumentText = documentTurn
      ? assistantPartRefs.findLast(
          ({ part }, index) => index > lastToolIndex && part.type === "text" && !!part.text.trim(),
        )?.part
      : undefined

    const downloads =
      finalDocumentText?.type === "text"
        ? translationDownloads(assistantPartRefs.map((ref) => ref.part)).filter((file) =>
            translationDownloadReferenced(finalDocumentText.text, file.uri),
          )
        : []

    const turnCollapsed = turnCollapsedOverride ?? !working
    const hasAssistantContent = assistantMessages.length > 0 || working

    if (hasAssistantContent) {
      rows.push(new TimelineRow.TurnStatus({ userMessageID: userMessage.id }))
    }

    // Split items: process (reasoning/tool/context) goes above the product (text)
    // so the final output is never interleaved with the thinking process. When
    // collapsed, only the product stays; when expanded, process then product.
    const processItems: (typeof assistantItems)[number][] = []
    const productItems: (typeof assistantItems)[number][] = []
    assistantItems.forEach((item) => {
      if (item.type === "interrupted") {
        processItems.push(item)
        return
      }
      const group = item.group
      if (group.type === "context") {
        processItems.push(item)
        return
      }
      const part = getMessageParts(group.ref.messageID).find((p) => p.id === group.ref.partID)
      const inlineDelivery =
        finalDocumentText?.type === "text" &&
        part?.type === "tool" &&
        translationDownloads([part]).some((file) => downloads.some((download) => download.uri === file.uri))
      if (
        part &&
        ((!inlineDelivery && toolHasProduct(part)) ||
          (part.type === "text" && (documentTurn ? part.id === finalDocumentText?.id : !hasTranslation)))
      ) {
        productItems.push(item)
        return
      }
      processItems.push(item)
    })

    const pushItem = (item: (typeof assistantItems)[number]) => {
      if (item.type === "interrupted") {
        rows.push(
          new TimelineRow.TurnDivider({
            userMessageID: userMessage.id,
            label: "interrupted",
          }),
        )
        return
      }
      rows.push(
        new TimelineRow.AssistantPart({
          userMessageID: userMessage.id,
          group: item.group,
          previousAssistantPart: assistantGroupIndex > 0,
        }),
      )
      assistantGroupIndex += 1
    }

    if (!turnCollapsed) {
      processItems.forEach(pushItem)
    }
    productItems.forEach(pushItem)

    if (isActive && status === "retry") rows.push(new TimelineRow.Retry({ userMessageID: userMessage.id }))

    const diffs = uniqueSummaryDiffs(userMessage.summary?.diffs)
    if (diffs.length > 0 && (status === "idle" || !isActive)) {
      rows.push(
        new TimelineRow.DiffSummary({
          userMessageID: userMessage.id,
          diffs,
        }),
      )
    }

    if (error) {
      const data = error.data?.message
      rows.push(
        new TimelineRow.Error({
          userMessageID: userMessage.id,
          text: unwrapErrorMessage(
            typeof data === "string" ? data : data === undefined || data === null ? "" : String(data),
          ),
        }),
      )
    }

    return rows
  }

  function unwrapErrorMessage(message: string) {
    const text = message.replace(/^Error:\s*/, "").trim()

    const parse = (value: string) => {
      try {
        return JSON.parse(value) as unknown
      } catch {
        return undefined
      }
    }

    const read = (value: string) => {
      const first = parse(value)
      if (typeof first !== "string") return first
      return parse(first.trim())
    }

    let json = read(text)

    if (json === undefined) {
      const start = text.indexOf("{")
      const end = text.lastIndexOf("}")
      if (start !== -1 && end > start) json = read(text.slice(start, end + 1))
    }

    if (!record(json)) return message

    const err = record(json.error) ? json.error : undefined
    if (err) {
      const type = typeof err.type === "string" ? err.type : undefined
      const msg = typeof err.message === "string" ? err.message : undefined
      if (type && msg) return `${type}: ${msg}`
      if (msg) return msg
      if (type) return type
      const code = typeof err.code === "string" ? err.code : undefined
      if (code) return code
    }

    const msg = typeof json.message === "string" ? json.message : undefined
    if (msg) return msg

    const reason = typeof json.error === "string" ? json.error : undefined
    if (reason) return reason

    return message
  }

  function record(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value)
  }
}

export namespace MessageComment {
  export type MessageComment = {
    path: string
    comment: string
    selection?: {
      startLine: number
      endLine: number
    }
  }

  export const fromPart = (part: Part): MessageComment | undefined => {
    if (part.type !== "text" || !part.synthetic) return
    const next = readCommentMetadata(part.metadata) ?? parseCommentNote(part.text)
    if (!next) return
    return {
      path: next.path,
      comment: next.comment,
      selection: next.selection
        ? {
            startLine: next.selection.startLine,
            endLine: next.selection.endLine,
          }
        : undefined,
    }
  }
}
